import styled from 'styled-components';
import { Badge } from '@shared/ui/Badge';
import { kadryDateTime, type KadryProcessingProfile, type KadrySecretMeta } from '@entities/kadry';

export const Row = styled.form`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(2)};

  input {
    flex: 1 1 240px;
    max-width: 420px;
  }
`;

export const Line = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 14px;
`;

/* Содержимое карточки: шаги и разделы с ровным зазором. */
export const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(5)};
`;

/* Шаг или раздел карточки: подпись и его поля — плотнее, чем между шагами. */
export const Step = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2.5)};

  & > h4 {
    margin: 0;
  }
`;

export const Code = styled.code`
  padding: 2px 6px;
  border-radius: ${({ theme }) => theme.radius.xs};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
  overflow-wrap: anywhere;
`;

export const Checklist = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 14px;

  & > li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
  }
`;

export const Steps = styled.ol`
  margin: 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* Действие строки чек-листа — у правого края. */
export const RowAction = styled.div`
  margin-left: auto;
`;

/* Форма утверждения профиля — отдельной плашкой на всю ширину под строкой,
   чтобы строка не сжималась и соседние не съезжали. */
export const ApproveForm = styled.form`
  flex: 1 0 100%;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};

  input {
    max-width: 560px;
  }
`;

/** Сохранён ли ключ приложения и когда (значение наружу не отдаётся). */
export const KeyState = ({ label, meta }: { label: string; meta: KadrySecretMeta | undefined }) => (
  <span>
    {label}:{' '}
    {meta ? (
      <Badge $tone="success">сохранён {kadryDateTime(meta.rotated_at)}</Badge>
    ) : (
      <Badge $tone="warning">не задан</Badge>
    )}
  </span>
);

export const Mark = ({ ok }: { ok: boolean }) => (
  <Badge $tone={ok ? 'success' : 'warning'}>{ok ? 'готово' : 'нужно'}</Badge>
);

export const REQUIRED_GATES: { gate: string; label: string }[] = [
  { gate: 'source.hh', label: 'Поиск на hh.ru' },
  { gate: 'source.rabota', label: 'Поиск на Работа.ру' },
  { gate: 'channel.platform_invite', label: 'Приглашения через площадки (hh.ru, Работа.ру)' },
  {
    gate: 'bot.autooffer_platform_search',
    label: 'Автопредложение: балл DeepSeek от порога — предложение уходит без кадровика, лучшим первым (порог и лимиты — «Предложения»)',
  },
  { gate: 'channel.telegram', label: 'Бот Telegram' },
  { gate: 'channel.max', label: 'Бот MAX' },
  { gate: 'channel.sms', label: 'SMS кандидату с предложением (SMS.ru)' },
  { gate: 'llm.external', label: 'Анализ DeepSeek (разрешено профилем обработки)' },
  { gate: 'llm.external_candidate_text', label: 'Анализ резюме DeepSeek (обезличенный текст)' },
];

export const PROFILES: { recipient: KadryProcessingProfile['recipient']; label: string }[] = [
  { recipient: 'hh', label: 'hh.ru — приглашения' },
  { recipient: 'rabota', label: 'Работа.ру — приглашения и контакты' },
  { recipient: 'telegram', label: 'Telegram — сообщения бота' },
  { recipient: 'max', label: 'MAX — сообщения бота' },
  { recipient: 'sms', label: 'SMS.ru — SMS с предложением' },
  { recipient: 'llm_external', label: 'DeepSeek — анализ обезличенных резюме' },
];

export type KadrySourceId = 'hh' | 'rabota';

export function monthRef(source: KadrySourceId): string {
  const d = new Date();
  return `${source}:${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Площадки с подключением работодателя по OAuth. */
export const SOURCE_UI: Record<
  KadrySourceId,
  {
    title: string;
    devUrl: string;
    devLabel: string;
    appHint: string;
    /** Показать домен (Работа.ру регистрирует домен), а не полный адрес возврата. */
    domainOnly: boolean;
    idSecret: string;
    secretSecret: string;
    idLabel: string;
    secretLabel: string;
    connectedNote: string;
  }
> = {
  hh: {
    title: 'hh.ru',
    devUrl: 'https://dev.hh.ru',
    devLabel: 'dev.hh.ru',
    appHint: '(тип — для работодателя) и укажите адрес перенаправления:',
    domainOnly: false,
    idSecret: 'hh.client_id',
    secretSecret: 'hh.client_secret',
    idLabel: 'Client ID',
    secretLabel: 'Client Secret',
    connectedNote: 'Аккаунт работодателя подключён — поиск по активным заявкам запустится автоматически.',
  },
  rabota: {
    title: 'Работа.ру',
    devUrl: 'https://dev.rabota.ru/applications/',
    devLabel: 'dev.rabota.ru',
    appHint: '(права — профиль и вакансии) и укажите домен приложения:',
    domainOnly: true,
    idSecret: 'rabota.app_id',
    secretSecret: 'rabota.secret',
    idLabel: 'ID приложения',
    secretLabel: 'Секрет приложения',
    connectedNote:
      'Аккаунт работодателя подключён — поиск по активным заявкам пойдёт и на Работа.ру.',
  },
};

export function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}
